import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {ControlValueAccessor, FormsModule} from '@angular/forms';
import {DateRange} from '../../../types/date-range';
import {NgForOf, NgIf} from '@angular/common';
import {TranslatePipe} from '@ngx-translate/core';

@Component({
  selector: 'app-date-range-selector',
  imports: [
    FormsModule,
    NgForOf,
    NgIf,
    TranslatePipe
  ],
  templateUrl: './date-range-selector.component.html',
  styleUrl: './date-range-selector.component.scss'
})
export class DateRangeSelectorComponent implements ControlValueAccessor, OnInit {

  @Input('date') dateSelector: string = 'this-month';
  @Input('timezone') timezone: string = '';
  @Input('shortTimezone') shortTimezone: boolean = false;
  @Input('showTimezone') showTimezone: boolean = true;
  @Output() dateChanged = new EventEmitter<DateRange>();
  @Output() dateRangeInvalidChange = new EventEmitter<boolean>();

  dateFrom: Date = new Date();
  dateTo: Date = new Date();

  dateFromSelector: string = '';
  dateToSelector: string = '';

  dateRangeInvalid: boolean = false;

  ngOnInit() {
    if (!this.timezone) {
      this.timezone = Math.round(-(new Date(2000, 0, 1)).getTimezoneOffset() / 60).toString();
    }

    this.onDateChange();
  }

  hasDst(): boolean {
    let jan = (new Date(2000, 0, 1)).getTimezoneOffset();
    let jul = (new Date(2000, 6, 1)).getTimezoneOffset();
    return jan != jul;
  }

  isCurrentlyDST() {
    const now = new Date();

    const jan = new Date(now.getFullYear(), 0, 1);
    const jul = new Date(now.getFullYear(), 6, 1);

    const standardOffset = Math.max(
      jan.getTimezoneOffset(),
      jul.getTimezoneOffset()
    );

    return now.getTimezoneOffset() < standardOffset;
  }

  getTimezones(): number[] {
    return Array.from({length: 27}, (_, i) => i - 12);
  }

  getTimezoneOffsetLabel(timezone: number): string {
    let offset = (this.hasDst() && this.isCurrentlyDST()) ? timezone + 1 : timezone;

    let text = '';
    if (offset > 0) {
      text = ' +' + offset.toString();
    } else if (offset < 0) {
      text = ' ' + offset.toString();
    } else {
      text = '';
    }

    if (this.shortTimezone) {
      return 'GMT' + text;
    }

    return '(GMT' + text + ') ';
  }

  computeDate(): void {
    let dateFrom: Date = new Date(this.dateFromSelector);
    let dateTo: Date = new Date(this.dateToSelector);
    const now = new Date();

    if (this.dateSelector === 'custom') {
      if (!this.dateFromSelector || !this.dateToSelector || isNaN(dateFrom.getDate()) || isNaN(dateTo.getDate()) || dateTo.getTime() < dateFrom.getTime()) {
        // If date is invalid, switch to this month date
        this.dateSelector = 'this-month';
      } else {
        dateTo.setUTCHours(23);
        dateTo.setUTCMinutes(59);
        dateTo.setUTCSeconds(59);
        dateTo.setUTCMilliseconds(999);
      }
    }

    switch (this.dateSelector) {
      case 'today':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'yesterday':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - 1, 23, 59, 59, 999));
        break;
      case 'this-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() + 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-week':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay() - 6));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate() - now.getDay(), 23, 59, 59, 999));
        break;
      case 'this-month':
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-month':
        const dayCount = new Date(now.getFullYear(), now.getMonth(), 0).getDate();
        dateFrom = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth() - 1, dayCount, 23, 59, 59, 999));
        break;
      case 'this-year':
        dateFrom = new Date(Date.UTC(now.getFullYear(), 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999));
        break;
      case 'last-year':
        dateFrom = new Date(Date.UTC(now.getFullYear() - 1, 0, 1));
        dateTo = new Date(Date.UTC(now.getFullYear() - 1, 11, 31, 23, 59, 59, 999));
        break;
    }

    const timezone = this.getTimezoneOffsetString();

    this.dateFromSelector = dateFrom.toISOString().split('T')[0];
    this.dateToSelector = dateTo.toISOString().split('T')[0];

    this.dateFrom = new Date(dateFrom.toISOString().split('Z')[0] + timezone);
    this.dateTo = new Date(dateTo.toISOString().split('Z')[0] + timezone);

    this.onChange({
      start: this.dateFrom,
      end: this.dateTo,
    });

    this.dateChanged.emit({
      start: this.dateFrom,
      end: this.dateTo,
    });
  }

  getTimezoneOffsetString(): string {
    const negativeTimezone = Number(this.timezone) < 0;
    const realTimezone = Number(this.timezone) + ((this.hasDst() && this.isCurrentlyDST()) ? 1 : 0);
    return (negativeTimezone ? '-' : '+') + Math.abs(Number(realTimezone)).toString().padStart(2, '0') + ':00';
  }

  onDateChange(): void {
    this.computeDate();

    this.dateRangeInvalid = this.dateTo < this.dateFrom;
    this.dateRangeInvalidChange.emit(this.dateRangeInvalid);

    if (!this.dateRangeInvalid) {
      this.dateChanged.emit({
        start: this.dateFrom,
        end: this.dateTo,
      });
    }
  }

  onCustomDateChange(): void {
    this.dateSelector = 'custom';

    this.onDateChange();
  }

  isCustomDate(): boolean {
    return this.dateSelector === 'custom';
  }

  getDateFrom(): Date {
    return this.dateFrom;
  }

  getDateTo(): Date {
    return this.dateTo;
  }

  getDateISO8601From(): string {
    return this.dateFromSelector + 'T00:00:00' + this.getTimezoneOffsetString();
  }

  getDateISO8601To(): string {
    return this.dateToSelector + 'T23:59:59' + this.getTimezoneOffsetString();
  }

  isValidDate(date: any): boolean {
    return !!(date && !isNaN(Date.parse(date)));
  }

  writeValue(obj: any): void {
    if (typeof obj === 'string') {
      this.dateSelector = obj;
    } else {
      if (this.isValidDate(obj?.from) && this.isValidDate(obj?.to)) {
        this.dateSelector = 'custom';
        this.dateFromSelector = obj?.from;
        this.dateToSelector = obj?.to;
      }
    }

    this.computeDate();
  }

  onChange = (_: any) => {
  };

  onBlur = (_: any) => {
  }

  registerOnChange(fn: (_: any) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onBlur = fn;
  }
}
